import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressBar } from 'primereact/progressbar';
import { extractResponseData, SmartHeader, NoRecordsCard } from './_SmartFieldRenderer';

const fmt = (v) => {
    if (v === null || v === undefined || v === '' || v === '-1' || v === -1) return '—';
    const num = Number(v);
    if (!isNaN(num) && String(v).trim() !== '') return '₹' + num.toLocaleString('en-IN');
    return String(v);
};

const fmtAmt = (v) => {
    if (v === null || v === undefined || v === '' || String(v) === '-1') return '—';
    const num = Number(v);
    if (!isNaN(num)) return '₹' + num.toLocaleString('en-IN');
    return String(v);
};

const fmtVal = (v) => (v === null || v === undefined || v === '' || String(v) === '-1' ? '—' : String(v));

const getScoreInfo = (score) => {
    const s = Number(score);
    if (isNaN(s)) return { severity: 'secondary', label: 'N/A', pct: 0 };
    const pct = Math.max(5, ((s - 300) / 600) * 100);
    if (s >= 750) return { severity: 'success', label: 'Excellent', pct };
    if (s >= 700) return { severity: 'success', label: 'Good', pct };
    if (s >= 650) return { severity: 'warning', label: 'Fair', pct };
    if (s >= 550) return { severity: 'warning', label: 'Below Average', pct };
    return { severity: 'danger', label: 'Poor', pct };
};

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        {children}
    </div>
);

const Field = ({ label, value, mono, highlight }) => (
    <div className="col-12 sm:col-6 md:col-4 mb-2">
        <div className="text-500 text-xs uppercase mb-1">{label}</div>
        <div className={`text-sm font-medium text-900${mono ? ' font-mono' : ''}${highlight ? ' text-primary' : ''}`}>
            {value || '—'}
        </div>
    </div>
);

const payStatusSeverity = (s) => {
    if (!s || s === '0' || s === 'STD') return 'success';
    if (s === 'XXX') return 'secondary';
    if (['SUB', 'DBT', 'LSS', 'SMA'].includes(s)) return 'danger';
    return 'warning';
};

const payStatusLabel = (s) => {
    const map = { '0': 'Current', 'STD': 'Standard', 'SMA': 'SMA', 'SUB': 'Sub-std', 'DBT': 'Doubtful', 'LSS': 'Loss', 'XXX': 'No data' };
    return map[s] || s || '?';
};

const MonthlyPayHistory = ({ history }) => {
    if (!Array.isArray(history) || history.length === 0) return null;
    return (
        <div>
            <div className="text-500 text-xs uppercase mb-2">Monthly Payment History</div>
            <div className="flex flex-wrap gap-1">
                {history.map((h, i) => (
                    <div key={i} className="text-center" style={{ minWidth: '48px' }}>
                        <Tag value={payStatusLabel(h.status)} severity={payStatusSeverity(h.status)} className="text-xs" style={{ fontSize: '0.65rem', padding: '2px 4px' }} />
                        <div className="text-500 mt-1" style={{ fontSize: '0.6rem' }}>{h.date ? h.date.slice(0, 7) : ''}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const STATE_CODES = {
    '01': 'Andhra Pradesh', '02': 'Arunachal Pradesh', '03': 'Assam', '04': 'Bihar',
    '05': 'Chhattisgarh', '06': 'Goa', '07': 'Gujarat', '08': 'Haryana',
    '09': 'Himachal Pradesh', '10': 'Jharkhand', '11': 'Jammu & Kashmir',
    '12': 'Karnataka', '13': 'Kerala', '14': 'Madhya Pradesh', '15': 'Maharashtra',
    '16': 'Manipur', '17': 'Meghalaya', '18': 'Mizoram', '19': 'Nagaland',
    '20': 'Odisha', '21': 'Punjab', '22': 'Rajasthan', '23': 'Sikkim',
    '24': 'Tamil Nadu', '25': 'Telangana', '26': 'Tripura', '27': 'Uttar Pradesh',
    '28': 'Andhra Pradesh', '29': 'West Bengal', '30': 'Delhi',
    '31': 'Chandigarh', '33': 'Tamil Nadu', '34': 'Pondicherry',
};

// `onDownloadPDF`/`pdfPasswordHint` come from SimpleReportForm, which does the
// authenticated blob download. Shared-link and lead-history views render this
// component without them and simply get no button — those readers have no
// business re-rendering somebody's credit report.
const CibilReportOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const [expandedRows, setExpandedRows] = useState(null);
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No records found for the provided details." />;
    }

    const d = responseData;

    // Surepass returns data nested under credit_report[0]
    const cr = Array.isArray(d.credit_report) && d.credit_report.length > 0 ? d.credit_report[0] : null;

    // Personal info — top-level fields + names array from credit_report
    const nameEntry = cr?.names?.[0] || {};
    const name = d.name || nameEntry.name || '';
    const gender = d.gender || nameEntry.gender || '';
    const dob = nameEntry.birthDate || d.date_of_birth || '';
    const pan = d.pan || '';
    const mobile = d.mobile || '';
    const email = d.user_email || d.email || cr?.emails?.[0]?.emailID || '';

    // Score
    const creditScore = d.credit_score || cr?.scores?.[0]?.score || '';
    const scoreInfo = getScoreInfo(creditScore);
    const scoreEntry = cr?.scores?.[0] || {};
    const reasonCodes = Array.isArray(scoreEntry.reasonCodes) ? scoreEntry.reasonCodes : [];

    // Accounts — from credit_report[0].accounts or top-level
    //
    // Stamped with a synthetic row key (#407). The accounts table tracks
    // row-expansion state by `dataKey`, and bureau account rows carry no unique
    // field: accountNumber arrives masked and repeats across lenders. With no
    // usable key every row resolved to the same value, so PrimeReact kept ONE
    // shared expansion entry and clicking any account's "Monthly Payment
    // History" expanded all of them at once.
    //
    // Deliberately `__rowIndex` rather than `index`: this is our key, not the
    // provider's data, and a name that cannot collide with a future CIBIL field
    // is what keeps it that way.
    const rawAccounts = Array.isArray(cr?.accounts) ? cr.accounts : Array.isArray(d.accounts) ? d.accounts : [];
    const accounts = rawAccounts.map((a, i) => ({ ...a, __rowIndex: i }));

    // Addresses — from credit_report[0].addresses or top-level
    const addresses = Array.isArray(cr?.addresses) ? cr.addresses : [];

    // IDs
    const ids = Array.isArray(cr?.ids) ? cr.ids : [];

    // Telephones
    const telephones = Array.isArray(cr?.telephones) ? cr.telephones : [];

    // Employment
    const employment = Array.isArray(cr?.employment) ? cr.employment : [];

    // Summary stats
    const activeAccounts = accounts.filter((a) => !a.dateClosed || a.dateClosed === 'NA').length;
    const closedAccounts = accounts.filter((a) => a.dateClosed && a.dateClosed !== 'NA').length;
    const overdueAccounts = accounts.filter((a) => Number(a.amountOverdue) > 0).length;
    const totalBalance = accounts.reduce((s, a) => s + (Number(a.currentBalance) > 0 ? Number(a.currentBalance) : 0), 0);

    const rowExpansion = (row) => (
        <div className="surface-50 border-round p-3">
            <MonthlyPayHistory history={row.monthlyPayStatus} />
        </div>
    );

    return (
        <div>
            <SmartHeader title="CIBIL Credit Report" icon="pi-chart-bar" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            <Card>
                {onDownloadPDF && (
                    <>
                        <div className="flex justify-content-end flex-wrap gap-2 mb-2">
                            <Button
                                label="Download PDF"
                                icon="pi pi-file-pdf"
                                size="small"
                                outlined
                                onClick={onDownloadPDF}
                            />
                        </div>
                        {pdfPasswordHint && (
                            <div className="surface-100 border-round border-left-3 border-primary p-2 mb-3 flex align-items-start gap-2">
                                <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                                <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                            </div>
                        )}
                    </>
                )}

                {/* Credit Score */}
                <Section icon="pi-star" title="Credit Score">
                    <div className="text-center surface-100 border-round p-4 mb-3">
                        <div className={`font-bold`} style={{
                            fontSize: '3.5rem',
                            lineHeight: 1,
                            color: scoreInfo.severity === 'success' ? '#22c55e' : scoreInfo.severity === 'warning' ? '#f97316' : scoreInfo.severity === 'danger' ? '#ef4444' : '#6b7280'
                        }}>
                            {creditScore || 'N/A'}
                        </div>
                        <Tag value={scoreInfo.label} severity={scoreInfo.severity} className="mt-2" />
                        <div className="mx-auto mt-3" style={{ maxWidth: '320px' }}>
                            <ProgressBar value={scoreInfo.pct} showValue={false} style={{ height: '0.5rem' }} />
                            <div className="flex justify-content-between text-500 text-xs mt-1">
                                <span>300</span><span>900</span>
                            </div>
                        </div>
                        {scoreEntry.scoreCardName && (
                            <div className="text-500 text-xs mt-2">{scoreEntry.scoreCardName} · {scoreEntry.scoreDate || ''}</div>
                        )}
                    </div>
                    {reasonCodes.length > 0 && (
                        <div>
                            <div className="text-500 text-xs uppercase mb-2">Score Reason Codes</div>
                            <div className="flex flex-wrap gap-2">
                                {reasonCodes.map((rc, i) => (
                                    <Tag key={i} value={`Code ${rc.reasonCodeValue}`} severity="secondary" className="text-xs" />
                                ))}
                            </div>
                        </div>
                    )}
                </Section>

                {/* Account Summary */}
                {accounts.length > 0 && (
                    <Section icon="pi-chart-pie" title="Account Summary">
                        <div className="grid text-center">
                            <div className="col-6 md:col-3">
                                <div className="surface-100 border-round p-3">
                                    <div className="text-blue-600 text-2xl font-bold">{accounts.length}</div>
                                    <div className="text-500 text-xs uppercase mt-1">Total Accounts</div>
                                </div>
                            </div>
                            <div className="col-6 md:col-3">
                                <div className="surface-100 border-round p-3">
                                    <div className="text-green-600 text-2xl font-bold">{activeAccounts}</div>
                                    <div className="text-500 text-xs uppercase mt-1">Active</div>
                                </div>
                            </div>
                            <div className="col-6 md:col-3">
                                <div className="surface-100 border-round p-3">
                                    <div className="text-500 text-2xl font-bold">{closedAccounts}</div>
                                    <div className="text-500 text-xs uppercase mt-1">Closed</div>
                                </div>
                            </div>
                            <div className="col-6 md:col-3">
                                <div className="surface-100 border-round p-3">
                                    <div className="text-red-600 text-2xl font-bold">{overdueAccounts}</div>
                                    <div className="text-500 text-xs uppercase mt-1">Overdue</div>
                                </div>
                            </div>
                        </div>
                        {totalBalance > 0 && (
                            <div className="mt-3 surface-100 border-round p-3 text-center">
                                <div className="text-500 text-xs uppercase mb-1">Total Outstanding Balance</div>
                                <div className="text-900 text-xl font-bold">₹{totalBalance.toLocaleString('en-IN')}</div>
                            </div>
                        )}
                    </Section>
                )}

                {/* Personal Information */}
                <Section icon="pi-user" title="Personal Information">
                    <div className="grid">
                        {name && <Field label="Name" value={name} />}
                        {dob && <Field label="Date of Birth" value={dob} />}
                        {gender && <Field label="Gender" value={gender} />}
                        {pan && <Field label="PAN" value={pan} mono highlight />}
                        {mobile && <Field label="Mobile" value={mobile} mono />}
                        {email && <Field label="Email" value={email} />}
                    </div>
                    {ids.length > 0 && (
                        <div className="mt-3">
                            <div className="text-500 text-xs uppercase mb-2">Linked IDs</div>
                            <div className="flex flex-wrap gap-2">
                                {ids.map((id, i) => (
                                    <div key={i} className="surface-100 border-round px-3 py-2">
                                        <div className="text-500 text-xs">{id.idType}</div>
                                        <div className="font-mono text-sm text-900 font-medium">{id.idNumber}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {telephones.length > 0 && (
                        <div className="mt-3">
                            <div className="text-500 text-xs uppercase mb-2">Registered Phone Numbers</div>
                            <div className="flex flex-wrap gap-2">
                                {[...new Map(telephones.map((t) => [t.telephoneNumber, t])).values()].map((t, i) => (
                                    <div key={i} className="surface-100 border-round px-3 py-2">
                                        <div className="font-mono text-sm text-900">{t.telephoneNumber?.replace(/^91/, '+91 ')}</div>
                                        <div className="text-500 text-xs">{t.telephoneType === '00' ? 'Mobile' : t.telephoneType === '01' ? 'Mobile' : `Type ${t.telephoneType}`}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Section>

                {/* Employment */}
                {employment.length > 0 && (
                    <Section icon="pi-briefcase" title="Employment">
                        <div className="grid">
                            {employment.map((e, i) => (
                                <div key={i} className="col-12 md:col-6">
                                    <div className="surface-100 border-round p-3">
                                        {e.name && <div className="text-900 font-medium mb-1">{e.name}</div>}
                                        <div className="grid">
                                            {e.accountType && <Field label="Type" value={e.accountType} />}
                                            {e.income && Number(e.income) > 0 && <Field label={`Income (${e.monthly_annual_income_indicator || ''})`} value={`₹${Number(e.income).toLocaleString('en-IN')}`} />}
                                            {e.net_gross_income_indicator && <Field label="Income Basis" value={e.net_gross_income_indicator} />}
                                            {e.dateReported && <Field label="Date Reported" value={e.dateReported} />}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {/* Loan Accounts */}
                {accounts.length > 0 && (
                    <Section icon="pi-list" title={`Loan Accounts (${accounts.length})`}>
                        <DataTable
                            value={accounts}
                            size="small"
                            stripedRows
                            showGridlines
                            responsiveLayout="scroll"
                            paginator={accounts.length > 10}
                            rows={10}
                            className="p-datatable-sm"
                            expandedRows={expandedRows}
                            onRowToggle={(e) => setExpandedRows(e.data)}
                            rowExpansionTemplate={rowExpansion}
                            dataKey="__rowIndex"
                        >
                            <Column expander style={{ width: '3rem' }} />
                            <Column field="memberShortName" header="Lender" style={{ minWidth: '120px' }} />
                            <Column field="accountType" header="Type" style={{ minWidth: '100px' }} />
                            <Column field="accountNumber" header="Account #" style={{ minWidth: '140px' }}
                                body={(row) => <span className="font-mono text-sm">{row.accountNumber}</span>} />
                            <Column field="dateOpened" header="Opened" style={{ minWidth: '90px' }} />
                            <Column field="currentBalance" header="Balance" style={{ minWidth: '90px' }}
                                body={(row) => <span>{fmtAmt(row.currentBalance)}</span>} />
                            <Column field="highCreditAmount" header="Sanctioned" style={{ minWidth: '90px' }}
                                body={(row) => <span>{fmtAmt(row.highCreditAmount)}</span>} />
                            <Column field="emiAmount" header="EMI" style={{ minWidth: '80px' }}
                                body={(row) => <span>{fmtAmt(row.emiAmount)}</span>} />
                            <Column field="amountOverdue" header="Overdue" style={{ minWidth: '80px' }}
                                body={(row) => {
                                    const v = Number(row.amountOverdue);
                                    if (isNaN(v) || v <= 0) return <span className="text-green-600">—</span>;
                                    return <span className="text-red-600 font-medium">₹{v.toLocaleString('en-IN')}</span>;
                                }} />
                            <Column field="dateClosed" header="Closed" style={{ minWidth: '90px' }}
                                body={(row) => <span>{row.dateClosed && row.dateClosed !== 'NA' ? row.dateClosed : '—'}</span>} />
                            <Column field="interest_rate" header="Rate%" style={{ minWidth: '70px' }}
                                body={(row) => <span>{fmtVal(row.interest_rate) === '—' ? '—' : `${row.interest_rate}%`}</span>} />
                        </DataTable>
                        <p className="text-500 text-xs mt-2">Click the expander icon to view monthly payment history for each account.</p>
                    </Section>
                )}

                {/* Addresses */}
                {addresses.length > 0 && (
                    <Section icon="pi-map-marker" title={`Addresses (${addresses.length})`}>
                        <div className="grid">
                            {addresses.map((a, i) => (
                                <div key={i} className="col-12 md:col-6 mb-2">
                                    <div className="surface-100 border-round p-3">
                                        <div className="text-900 text-sm font-medium">
                                            {[a.line1, a.line2].filter(Boolean).join(', ')}
                                        </div>
                                        <div className="text-500 text-xs mt-1">
                                            {[STATE_CODES[a.stateCode] || `State ${a.stateCode}`, a.pinCode].filter(Boolean).join(' — ')}
                                        </div>
                                        {a.dateReported && <div className="text-500 text-xs mt-1">Reported: {a.dateReported}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

            </Card>
        </div>
    );
};

export default CibilReportOutputComponent;
